// src/pages/Connector/PrayerTiming/PrayerTimingConfigForm.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Grid,
    Alert,
    CircularProgress,
    Typography,
    Divider,
    Autocomplete
} from '@mui/material';
import axiosInstance from "../../../../utils/axiosInstance";

// Custom filter function that prioritizes exact matches, then "starts with", then substring matches
const filterOptions = (options, { inputValue }) => {
    const input = inputValue.toLowerCase().trim();
    if (!input) return options;

    // Separate into categories
    const exactMatches = [];
    const startsWithMatches = [];
    const containsMatches = [];

    options.forEach(option => {
        const label = (option.label || '').toLowerCase();
        if (label === input) {
            exactMatches.push(option);
        } else if (label.startsWith(input)) {
            startsWithMatches.push(option);
        } else if (label.includes(input)) {
            containsMatches.push(option);
        }
    });

    // Return in priority order: exact -> starts with -> contains
    return [...exactMatches, ...startsWithMatches, ...containsMatches];
};

const CALCULATION_METHODS = [
    { value: 'ISNA', label: 'ISNA - Islamic Society of North America' },
    { value: 'MWL', label: 'MWL - Muslim World League' },
    { value: 'Egypt', label: 'Egypt - Egyptian General Authority of Survey' },
    { value: 'Makkah', label: 'Makkah - Umm al-Qura University' },
    { value: 'Karachi', label: 'Karachi - University of Islamic Sciences' },
    { value: 'Tehran', label: 'Tehran - Institute of Geophysics' },
    { value: 'Jafari', label: 'Jafari - Shia Ithna Ashari' },
];

const AUTO_TRAIN_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

export default function PrayerTimingConfigForm({ open, onClose, onSave, config }) {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        city: '',
        country: '',
        latitude: '',
        longitude: '',
        calculation_method: 'ISNA',
        auto_train_enabled: false,
        auto_train_day_of_month: 1,
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Dropdown data states
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    // Selected objects for Autocomplete
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);

    // City search state
    const [citySearchInput, setCitySearchInput] = useState('');

    // Ref to track the latest city fetch request and cancel stale ones
    const cityFetchIdRef = useRef(0);
    // Ref to track debounce timeout for city search
    const citySearchTimeoutRef = useRef(null);

    // Fetch countries
    const fetchCountries = useCallback(async () => {
        setLoadingCountries(true);
        try {
            const response = await axiosInstance.get('/api/selections/countries');
            if (response.data.success) {
                setCountries(response.data.data || []);
            }
        } catch (err) {
            console.error('Error fetching countries:', err);
        } finally {
            setLoadingCountries(false);
        }
    }, []);

    // Fetch cities by country with optional search
    const fetchCities = useCallback(async (countryId, searchTerm = '') => {
        if (!countryId) {
            setCities([]);
            return;
        }

        // Increment fetch ID to track this request
        const fetchId = ++cityFetchIdRef.current;

        setLoadingCities(true);
        try {
            const params = searchTerm ? { search: searchTerm } : {};
            const response = await axiosInstance.get(`/api/selections/countries/${countryId}/cities`, { params });

            // Only update state if this is still the latest request
            if (fetchId === cityFetchIdRef.current && response.data.success) {
                setCities(response.data.data || []);
            }
        } catch (err) {
            // Only log error if this is still the latest request
            if (fetchId === cityFetchIdRef.current) {
                console.error('Error fetching cities:', err);
            }
        } finally {
            // Only update loading state if this is still the latest request
            if (fetchId === cityFetchIdRef.current) {
                setLoadingCities(false);
            }
        }
    }, []);

    // Load countries on mount
    useEffect(() => {
        if (open) {
            fetchCountries();
        }
    }, [open, fetchCountries]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (citySearchTimeoutRef.current) {
                clearTimeout(citySearchTimeoutRef.current);
            }
        };
    }, []);

    // Reset city state when country changes and fetch initial cities
    useEffect(() => {
        // Clear any pending search timeout
        if (citySearchTimeoutRef.current) {
            clearTimeout(citySearchTimeoutRef.current);
            citySearchTimeoutRef.current = null;
        }

        if (selectedCountry?.id) {
            setCitySearchInput('');
            setSelectedCity(null);
            // Fetch initial cities for the selected country
            fetchCities(selectedCountry.id, '');
        } else {
            setCities([]);
            setSelectedCity(null);
        }
    }, [selectedCountry, fetchCities]);

    // Function to trigger city search with debounce
    const triggerCitySearch = useCallback((searchTerm) => {
        // Clear any existing timeout
        if (citySearchTimeoutRef.current) {
            clearTimeout(citySearchTimeoutRef.current);
        }

        // Set new timeout for debounced search (150ms for faster response)
        citySearchTimeoutRef.current = setTimeout(() => {
            if (selectedCountry?.id) {
                fetchCities(selectedCountry.id, searchTerm);
            }
        }, 150);
    }, [selectedCountry, fetchCities]);

    // Initialize form when config changes
    useEffect(() => {
        if (config) {
            setFormData({
                title: config.title || '',
                description: config.description || '',
                city: config.city || '',
                country: config.country || '',
                latitude: config.latitude || '',
                longitude: config.longitude || '',
                calculation_method: config.calculation_method || 'ISNA',
                auto_train_enabled: config.auto_train_enabled || false,
                auto_train_day_of_month: config.auto_train_day_of_month || 1,
            });

            // Set selected country if exists
            if (config.country && countries.length > 0) {
                const countryObj = countries.find(c => c.label === config.country);
                setSelectedCountry(countryObj || null);
            }
        } else {
            setFormData({
                title: '',
                description: '',
                city: '',
                country: '',
                latitude: '',
                longitude: '',
                calculation_method: 'ISNA',
                auto_train_enabled: false,
                auto_train_day_of_month: 1,
            });
            setSelectedCountry(null);
            setSelectedCity(null);
        }
        setErrors({});
        setSubmitError('');
    }, [config, open, countries]);

    // Set selected city when cities are loaded and config has city
    useEffect(() => {
        if (config?.city && cities.length > 0) {
            const cityObj = cities.find(c => c.label === config.city);
            setSelectedCity(cityObj || null);
        }
    }, [config, cities]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!selectedCountry) {
            newErrors.country = 'Country is required';
        }
        if (!selectedCity) {
            newErrors.city = 'City is required';
        }

        // Validate coordinates (auto-populated from city, but can be manually adjusted)
        if (!formData.latitude) {
            newErrors.latitude = 'Latitude is required';
        } else if (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90) {
            newErrors.latitude = 'Invalid latitude (-90 to 90)';
        }
        if (!formData.longitude) {
            newErrors.longitude = 'Longitude is required';
        } else if (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180) {
            newErrors.longitude = 'Invalid longitude (-180 to 180)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validate()) return;

        setIsSubmitting(true);

        try {
            const submitData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                calculation_method: formData.calculation_method,
                auto_train_enabled: formData.auto_train_enabled,
                auto_train_day_of_month: formData.auto_train_day_of_month,
                city: formData.city.trim(),
                country: formData.country.trim(),
                latitude: parseFloat(formData.latitude),
                longitude: parseFloat(formData.longitude),
            };

            await onSave(submitData);
        } catch (err) {
            setSubmitError(err.response?.data?.message || 'Failed to save configuration');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {config ? 'Edit Prayer Timing Configuration' : 'Add Prayer Timing Configuration'}
                </DialogTitle>
                <DialogContent>
                    {submitError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {submitError}
                        </Alert>
                    )}

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Basic Info */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Title (e.g., Mosque Name)"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                error={!!errors.title}
                                helperText={errors.title}
                                required
                                placeholder="e.g., Al-Farooq Mosque"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={3}
                                placeholder="Enter details about this location, contact info, etc."
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Location
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={countries}
                                getOptionLabel={(option) => option.label || ''}
                                filterOptions={filterOptions}
                                value={selectedCountry}
                                onChange={(_, newValue) => {
                                    setSelectedCountry(newValue);
                                    setFormData(prev => ({
                                        ...prev,
                                        country: newValue?.label || '',
                                        // Clear city and coordinates when country changes
                                        city: '',
                                        latitude: '',
                                        longitude: ''
                                    }));
                                    setSelectedCity(null);
                                    if (errors.country) {
                                        setErrors(prev => ({ ...prev, country: '' }));
                                    }
                                }}
                                loading={loadingCountries}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Country"
                                        required
                                        error={!!errors.country}
                                        helperText={errors.country}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingCountries ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                // Key forces remount when country changes
                                key={`city-select-${selectedCountry?.id || 'none'}`}
                                options={cities}
                                getOptionLabel={(option) => option.label || ''}
                                filterOptions={(x) => x}
                                value={selectedCity}
                                inputValue={citySearchInput}
                                autoHighlight
                                openOnFocus
                                clearOnBlur={false}
                                blurOnSelect
                                onChange={(_, newValue) => {
                                    setSelectedCity(newValue);
                                    // Update input to show selected city name
                                    setCitySearchInput(newValue?.label || '');
                                    setFormData(prev => ({
                                        ...prev,
                                        city: newValue?.label || '',
                                        // Auto-populate coordinates from city data
                                        latitude: newValue?.lat || '',
                                        longitude: newValue?.lng || ''
                                    }));
                                    if (errors.city) {
                                        setErrors(prev => ({ ...prev, city: '', latitude: '', longitude: '' }));
                                    }
                                }}
                                onInputChange={(_, newInputValue, reason) => {
                                    // Always update input value to keep it controlled
                                    setCitySearchInput(newInputValue);
                                    // Trigger search when user types
                                    if (reason === 'input') {
                                        // Clear current cities immediately to show loading state
                                        // This prevents showing stale results while search is pending
                                        if (newInputValue.length > 0) {
                                            // Invalidate any pending requests by incrementing the ID
                                            cityFetchIdRef.current++;
                                            setCities([]);
                                            setLoadingCities(true);
                                        }
                                        triggerCitySearch(newInputValue);
                                    }
                                }}
                                loading={loadingCities}
                                disabled={!selectedCountry}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="City"
                                        placeholder="Type to search cities..."
                                        required
                                        error={!!errors.city}
                                        helperText={errors.city || (!selectedCountry ? 'Select a country first' : '')}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Latitude"
                                name="latitude"
                                type="number"
                                inputProps={{ step: "any", min: -90, max: 90 }}
                                value={formData.latitude}
                                onChange={handleChange}
                                error={!!errors.latitude}
                                helperText={errors.latitude || 'Auto-populated from city selection'}
                                required
                                placeholder="e.g., 40.7128"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Longitude"
                                name="longitude"
                                type="number"
                                inputProps={{ step: "any", min: -180, max: 180 }}
                                value={formData.longitude}
                                onChange={handleChange}
                                error={!!errors.longitude}
                                helperText={errors.longitude || 'Auto-populated from city selection'}
                                required
                                placeholder="e.g., -74.0060"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Calculation Settings
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Calculation Method</InputLabel>
                                <Select
                                    name="calculation_method"
                                    value={formData.calculation_method}
                                    onChange={handleChange}
                                    label="Calculation Method"
                                >
                                    {CALCULATION_METHODS.map((method) => (
                                        <MenuItem key={method.value} value={method.value}>
                                            {method.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Auto Training Settings
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.auto_train_enabled}
                                        onChange={handleChange}
                                        name="auto_train_enabled"
                                    />
                                }
                                label="Enable Auto-Training (Fetch & train next month's prayer times automatically)"
                            />
                        </Grid>

                        {formData.auto_train_enabled && (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Auto Train Day of Month</InputLabel>
                                    <Select
                                        name="auto_train_day_of_month"
                                        value={formData.auto_train_day_of_month}
                                        onChange={handleChange}
                                        label="Auto Train Day of Month"
                                    >
                                        {AUTO_TRAIN_DAYS.map((day) => (
                                            <MenuItem key={day} value={day}>
                                                Day {day}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="text.secondary">
                                    Prayer times for next month will be fetched and trained on this day.
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : (config ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
